import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";

const steps = [
  { num: "01", icon: "ri-file-list-3-line", key: "step1" },
  { num: "02", icon: "ri-calendar-schedule-line", key: "step2" },
  { num: "03", icon: "ri-home-smile-2-line", key: "step3" },
];

const ProcessSection = () => {
  const { t } = useTranslation();
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const items = entry.target.querySelectorAll(".process-item");
            items.forEach((item, i) => {
              setTimeout(() => {
                item.classList.add("opacity-100", "translate-y-0");
                item.classList.remove("opacity-0", "translate-y-10");
              }, i * 150);
            });
          }
        });
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const scrollTo = (id: string) => {
    const el = document.getElementById(id);
    if (el) {
      const offset = 80;
      const top = el.getBoundingClientRect().top + window.scrollY - offset;
      window.scrollTo({ top, behavior: "smooth" });
    }
  };

  return (
    <section id="process" className="py-24 bg-white" ref={sectionRef}>
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="text-center mb-16">
          <span className="text-[#D4AF37] text-xs font-bold uppercase tracking-widest mb-3 block">
            {t("process_label")}
          </span>
          <h2 className="text-4xl md:text-5xl font-bold text-[#0F2A44] mb-4">
            {t("process_headline")}
          </h2>
          <p className="text-gray-500 text-lg max-w-lg mx-auto">
            {t("process_sub")}
          </p>
        </div>

        {/* Steps */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 relative">
          {/* Connector Line */}
          <div className="hidden md:block absolute top-12 left-1/4 right-1/4 h-px bg-[#D4AF37]/30" />

          {steps.map((step, idx) => (
            <div
              key={step.key}
              className="process-item opacity-0 translate-y-10 transition-all duration-600 text-center"
            >
              {/* Number Circle */}
              <div className="relative inline-block mb-6">
                <div className="w-24 h-24 flex items-center justify-center rounded-full border-2 border-[#D4AF37] bg-white mx-auto">
                  <span className="text-3xl font-bold text-[#D4AF37]">{step.num}</span>
                </div>
                {idx < steps.length - 1 && (
                  <div className="hidden md:block absolute top-1/2 -right-1/2 w-full h-px border-t-2 border-dashed border-[#D4AF37]/30 -translate-y-1/2" />
                )}
              </div>

              {/* Icon */}
              <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-[#0F2A44]/5 mx-auto mb-4">
                <i className={`${step.icon} text-xl text-[#0F2A44]`} />
              </div>

              <h3 className="text-xl font-bold text-[#0F2A44] mb-3">
                {t(`process_${step.key}_title`)}
              </h3>
              <p className="text-gray-500 text-sm leading-relaxed max-w-xs mx-auto">
                {t(`process_${step.key}_desc`)}
              </p>
            </div>
          ))}
        </div>

        {/* CTA */}
        <div className="text-center mt-14">
          <button
            onClick={() => scrollTo("contact")}
            className="inline-flex items-center gap-2 bg-[#D4AF37] hover:bg-[#B8952E] text-[#0F2A44] font-bold px-10 py-4 rounded-full text-base transition-all duration-200 hover:scale-105 whitespace-nowrap cursor-pointer"
            style={{ boxShadow: "0 4px 20px rgba(212,175,55,0.3)" }}
          >
            {t("process_cta")}
            <i className="ri-arrow-right-line" />
          </button>
        </div>
      </div>
    </section>
  );
};

export default ProcessSection;
